import { createTheme } from "@mui/material/styles";
import { champ, couleurs, couleursAdmin, polices } from "./tokens";

/**
 * Variantes maison du bouton, en remplacement des classes SCSS
 * `.btn.btn-blue` / `.btn.btn-red` :
 *   variant="cta"         bouton d'action principal (bleu marine, texte blanc)
 *   variant="ctaDanger"   action destructrice / annulation (rouge)
 *   variant="ctaSuccess"  action positive / confirmation (vert)
 * `size="small"` reste géré par MUI.
 */
declare module "@mui/material/Button" {
    interface ButtonPropsVariantOverrides {
        cta: true;
        ctaDanger: true;
        ctaSuccess: true;
    }
}

// Repris de assets/scss/component/_btn.scss (`.btn`) : pilule capitale, texte
// 16px, pleine largeur sous 425px. Les paddings XXL de bureau (`1rem 4rem`)
// sont ramenés à une valeur unique et sobre.
const ctaBase = {
    textTransform: "uppercase" as const,
    borderRadius: 25,
    fontFamily: polices.gras,
    fontWeight: 700,
    fontSize: "1rem",
    lineHeight: "1rem",
    letterSpacing: "-0.024em",
    padding: "10px 50px",
    "@media (max-width:424.95px)": {
        width: "100%",
        padding: "10px 20px",
    },
    "&.Mui-disabled": {
        backgroundColor: couleurs.GrisMoyen,
        color: couleurs.Blanc,
    },
};

const ctaSmall = {
    fontSize: "0.875rem",
    padding: "8px 24px",
    "@media (max-width:424.95px)": { width: "100%" },
};

const ctaBlue = {
    backgroundColor: couleurs.BleuMarin,
    color: couleurs.Blanc,
    "&:hover, &:focus": {
        backgroundColor: couleurs.BleuClair,
        color: couleurs.BleuMarin,
    },
};

const ctaRed = {
    backgroundColor: couleurs.Rouge,
    color: couleurs.Blanc,
    "&:hover, &:focus": {
        backgroundColor: couleurs.BleuMarin,
        color: couleurs.Blanc,
    },
};

const ctaGreen = {
    backgroundColor: couleurs.Vert,
    color: couleurs.BleuMarin,
    "&:hover, &:focus": {
        backgroundColor: couleurs.BleuMarin,
        color: couleurs.Blanc,
    },
};

/**
 * Thème MUI de l'admin — source unique du style des composants MUI.
 *
 * Avant, chaque écran contredisait les défauts de MUI en `sx` inline ou via du
 * SCSS `!important` ciblant les classes internes de MUI. Tout ce qui est
 * transversal (champs, boutons, tableaux, dialogues) se règle désormais ici.
 *
 * Les valeurs viennent de `theme/tokens.ts`, aligné sur l'app mobile.
 */
const theme = createTheme({
    palette: {
        mode: "light",
        primary: { main: couleurs.BleuMarin, contrastText: couleurs.Blanc },
        // Le jaune n'est plus utilisé pour les titres ni les boutons : la
        // couleur d'accent de l'admin est le bleu clair. `couleurs.Jaune`
        // reste disponible pour les accents historiques (stepper, hero…).
        secondary: { main: couleurs.BleuClair, contrastText: couleurs.BleuMarin },
        info: { main: couleurs.BleuClair, contrastText: couleurs.Blanc },
        error: { main: couleurs.Rouge, contrastText: couleurs.Blanc },
        success: { main: couleurs.Vert, contrastText: couleurs.BleuMarin },
        warning: { main: couleurs.Orange, contrastText: couleurs.Blanc },
        background: { default: couleursAdmin.FondPage, paper: couleurs.Blanc },
        text: { primary: couleurs.Texte, secondary: couleursAdmin.TexteDoux },
        divider: couleurs.GrisBordure,
    },

    // Points de rupture historiques du projet — ne pas modifier sans revoir
    // toutes les grilles (Grid md/lg) des écrans.
    breakpoints: {
        values: { xs: 0, sm: 768, md: 1024, lg: 1200, xl: 1440 },
    },

    typography: {
        fontFamily: polices.base,
    },

    shape: {
        borderRadius: 8,
    },

    components: {
        // ---------------------------------------------------------------
        // Champs de formulaire — style repris de l'app (stylesChamps) :
        // pilule blanche bordée, 50px de haut, texte 16px centré.
        // ---------------------------------------------------------------
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: couleurs.Blanc,
                    borderRadius: champ.rayon,
                    fontFamily: polices.base,
                    fontSize: champ.tailleTexte,
                    color: couleurs.Texte,
                    "&:not(.MuiInputBase-multiline)": {
                        height: champ.hauteur,
                        minHeight: champ.hauteur,
                        alignItems: "center",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: couleurs.BleuClair,
                        borderWidth: 1,
                    },
                    "&:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline": {
                        borderColor: couleursAdmin.BordureCarte,
                    },
                },
                // MUI décale le contour de -5px pour l'encoche du label
                // flottant, qu'on n'utilise pas (les libellés sont rendus
                // au-dessus du champ) : on le recale sur le champ.
                notchedOutline: {
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    border: `1px solid ${couleursAdmin.BordureCarte}`,
                    borderRadius: champ.rayon,
                    boxShadow: "none",
                    padding: 0,
                    "& legend": { display: "none" },
                },
                input: {
                    padding: `${champ.paddingVertical}px ${champ.paddingHorizontal}px`,
                    fontFamily: polices.base,
                    fontSize: champ.tailleTexte,
                    color: couleurs.Texte,
                    "&::placeholder": {
                        color: couleurs.GrisTexte,
                        opacity: 1,
                    },
                },
            },
        },

        MuiSelect: {
            styleOverrides: {
                // Laisse la place à la flèche du menu déroulant.
                select: { paddingRight: "40px !important" },
            },
        },

        MuiFormHelperText: {
            styleOverrides: {
                root: { fontFamily: polices.base, marginLeft: 4 },
            },
        },

        // ---------------------------------------------------------------
        // Boutons
        // ---------------------------------------------------------------
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: {
                    borderRadius: champ.rayon,
                    fontFamily: polices.gras,
                    fontWeight: 700,
                    letterSpacing: "-0.024em",
                    paddingLeft: 24,
                    paddingRight: 24,
                },
                sizeSmall: { paddingLeft: 16, paddingRight: 16 },
            },
            variants: [
                { props: { variant: "cta" }, style: { ...ctaBase, ...ctaBlue } },
                {
                    props: { variant: "cta", size: "small" },
                    style: ctaSmall,
                },
                {
                    props: { variant: "ctaDanger" },
                    style: { ...ctaBase, ...ctaRed },
                },
                {
                    props: { variant: "ctaDanger", size: "small" },
                    style: ctaSmall,
                },
                {
                    props: { variant: "ctaSuccess" },
                    style: { ...ctaBase, ...ctaGreen },
                },
                {
                    props: { variant: "ctaSuccess", size: "small" },
                    style: ctaSmall,
                },
            ],
        },

        MuiIconButton: {
            styleOverrides: {
                root: { borderRadius: "50%" },
            },
        },

        // Le tiroir permanent de l'admin est bordé à droite par défaut : on
        // le retire ici (remplace 3 sélecteurs `.css-<hash>` figés dans
        // assets/scss/base/_base.scss).
        MuiDrawer: {
            styleOverrides: {
                paper: { border: 0 },
            },
        },

        // ---------------------------------------------------------------
        // Tableaux de liste
        // ---------------------------------------------------------------
        MuiTableCell: {
            styleOverrides: {
                // Séparateurs de rangs ET de colonnes pour tous les tableaux
                // admin — même gris (--ultraLightGray) et même principe que
                // `.alert-line` sur le tableau de bord. `borderBottom` fixé
                // en toutes lettres (pas juste `borderColor`) : le défaut MUI
                // pose déjà un `borderBottom` avec une couleur très éclaircie
                // (`lighten(divider, 0.88)`), donc un simple `borderColor` ne
                // suffisait pas à le rendre visible.
                root: {
                    fontFamily: polices.base,
                    borderBottom: `1px solid ${couleurs.GrisTresClair}`,
                    borderRight: `1px solid ${couleurs.GrisTresClair}`,
                    "&:last-child": { borderRight: 0 },
                },
                head: {
                    fontFamily: polices.gras,
                    fontWeight: 700,
                    fontSize: 14,
                    lineHeight: "20px",
                    color: couleurs.BleuMarin,
                },
            },
        },

        MuiTableRow: {
            styleOverrides: {
                // Pas de trait sous la dernière ligne — même sortie de
                // tableau « propre » que `.alert-line` sur le tableau de
                // bord, désormais commune à tous les tableaux de liste.
                // `borderBottom` seulement : les séparateurs de colonnes
                // (`borderRight`, posé sur toutes les cellules) restent
                // visibles jusqu'à la dernière ligne.
                root: {
                    "&:last-child td, &:last-child th": { borderBottom: 0 },
                },
            },
        },

        // ---------------------------------------------------------------
        // Dialogues
        // ---------------------------------------------------------------
        MuiDialog: {
            styleOverrides: {
                paper: { borderRadius: 16 },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontFamily: polices.extraGras,
                    fontWeight: 800,
                    color: couleurs.BleuMarin,
                },
            },
        },

        MuiChip: {
            styleOverrides: {
                root: { fontFamily: polices.base },
            },
        },
    },
});

export default theme;
